import "./Category.css";
import CategoryDatabase from "./CategoryDatabase";
import CategoryDefinition from "./CategoryDefinition";
import { useLocation, useNavigate } from "react-router-dom";
import { useContext, useEffect, useMemo, useState } from "react";
import ConfigurationTabs from "../../components/ConfigurationTabs";
import { useTranslation } from "react-i18next";
import Modelos from "../../components/Modelos";
import IconLibrary from "../AssistantConfiguration/ConfigurationModule/IconLibrary";
import { loadIcons } from "../../utils/icons";
import SecondBox from "../../components/SecondBox";
import FileUpload from "../../components/FileUpload";
import { AuthContext } from "../../authContext";

function Category({ user, sessionId, totalCost, setTotalCost, setAssistant }) {
  const { t } = useTranslation("database");
  const { t: tSelfAssist } = useTranslation("assistantConfiguration");
  const { accessToken, email, setAssistantList } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const openModelOnLoad = Boolean(location.state?.openModelOnLoad);
  const createMode = Boolean(location.state?.createMode);
  const [assistantId, setAssistantId] = useState(
    location.state?.assistantId || "",
  );
  const isEditingClassifier = Boolean(assistantId);
  const [activeTab, setActiveTab] = useState("tab1");
  const [selectedModel, setSelectedModel] = useState("gemini-3.5-flash-lite");
  const icons = useMemo(() => loadIcons(), []);
  const [activeIcon, setActiveIcon] = useState(null);
  const [isXlsxProcessing, setIsXlsxProcessing] = useState(false);
  const [isXlsxPanelOpen, setIsXlsxPanelOpen] = useState(false);
  const [xlsxColumns, setXlsxColumns] = useState([]);
  const [selectedXlsxColumns, setSelectedXlsxColumns] = useState([]);
  const [xlsxUploadError, setXlsxUploadError] = useState("");
  const [editingSnapshot, setEditingSnapshot] = useState(null);
  const [configUpdatedAt, setConfigUpdatedAt] = useState(Date.now());

  const restoreEditingSnapshot = () => {
    if (!assistantId || !editingSnapshot) {
      return;
    }

    setAssistantData({
      assistant: {
        name: editingSnapshot.name || "",
        description: editingSnapshot.description || "",
      },
    });

    setSelectedModel(editingSnapshot.model || "gemini-3.5-flash-lite");
    setPromptText(editingSnapshot.prompt_user || "");
    setAssistantType(editingSnapshot.assistantType || "");
    setInputsSelected(
      editingSnapshot.inputsSelected || {
        pdf: false,
        word: false,
        excel: false,
      },
    );
    setOutputsSelected(
      editingSnapshot.outputsSelected || {
        excel: false,
        json: false,
        csv: false,
      },
    );
    setActiveIcon(editingSnapshot.activeIcon || null);
    setSelectedXlsxColumns(editingSnapshot.selectedXlsxColumns || []);
    setIsXlsxPanelOpen(false);
    setXlsxColumns([]);
    setXlsxUploadError("");
    setIsXlsxProcessing(false);
    setShowValidationErrors(false);
  };

  const resetXlsxFlow = () => {
    setIsXlsxProcessing(false);
    setIsXlsxPanelOpen(false);
    setXlsxColumns([]);
    setSelectedXlsxColumns([]);
    setXlsxUploadError("");
  };

  const handleXlsxUpload = async (files) => {
    const file = files?.[0];

    if (!file) {
      return;
    }

    setXlsxUploadError("");
    setSelectedXlsxColumns([]);
    setXlsxColumns([]);
    setIsXlsxProcessing(true);

    const formData = new FormData();
    formData.append("file", file);

    if (assistantId) {
      formData.append("assistant_id", assistantId);
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/post_xlsx_columns`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Erro ao processar arquivo XLSX.");
      }

      const data = await response.json();
      const columns = Array.isArray(data) ? data : [];
      setXlsxColumns(columns);
    } catch (error) {
      console.error("Erro ao buscar colunas do XLSX:", error);
      setXlsxUploadError("Nao foi possivel processar o arquivo XLSX.");
    } finally {
      setIsXlsxProcessing(false);
    }
  };

  const handleToggleXlsxColumn = (column) => {
    setSelectedXlsxColumns((prev) =>
      prev.includes(column)
        ? prev.filter((item) => item !== column)
        : [...prev, column],
    );
  };

  const handleToggleAllXlsxColumns = () => {
    const allSelected =
      xlsxColumns.length > 0 &&
      selectedXlsxColumns.length === xlsxColumns.length;

    if (allSelected) {
      setSelectedXlsxColumns([]);
      return;
    }

    setSelectedXlsxColumns([...xlsxColumns]);
  };

  const handleSaveXlsxColumns = () => {
    const columnsToSave = [...selectedXlsxColumns];
    setAssistantData((prev) => ({
      ...prev,
      xlsx_columns: columnsToSave,
    }));
    setIsXlsxPanelOpen(false);
  };

  // Estado local do assistente para permitir edição dos inputs em SecondBox
  const [assistantData, setAssistantData] = useState({ assistant: { name: "", description: "" } });

  const handleUpdateAssistant = (group, field, value) => {
    if (group === "assistant") {
      setAssistantData((prev) => ({
        ...prev,
        assistant: {
          ...prev.assistant,
          [field]: value,
        },
      }));
    }
  };

  const handleIconUpdate = (namespace, key, value) => {
    // placeholder: integrar atualização do assistente quando necessário
    console.debug("Icon update", namespace, key, value);
  };

  const [isPopupOpen, setIsPopupOpen] = useState(openModelOnLoad && !assistantId);
  const openPopup = () => setIsPopupOpen(true);
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const openPrompt = () => setIsPromptOpen(true);
  const [promptText, setPromptText] = useState("");

  const resetPromptContentState = () => {
    setIsPromptOpen(false);
    setPromptText("");
  };

  const resetModelContentState = () => {
    setSelectedModel("gemini-3.5-flash-lite");
    setActiveIcon(null);
    setAssistantData({ assistant: { name: "", description: "" } });
    setAssistantType("");
    setInputsSelected({ pdf: false, word: false, excel: false });
    setOutputsSelected({ excel: false, json: false, csv: false });
    setShowValidationErrors(false);
    resetXlsxFlow();
  };

  const closePopup = () => {
    if (isSavingClassifier) {
      return;
    }

    if (!assistantId && createMode) {
      navigate("/hub");
      return;
    }

    setIsPopupOpen(false);

    if (assistantId) {
      restoreEditingSnapshot();
      return;
    }

    if (!assistantId) {
      resetPromptContentState();
      resetModelContentState();
    }
  };

  const closeUploadPanel = () => {
    setInputsSelected((prev) => ({ ...prev, excel: false }));
    resetXlsxFlow();
  };

  const closePrompt = () => {
    if (isSavingClassifier) {
      return;
    }

    if (!assistantId && createMode) {
      navigate("/hub");
      return;
    }

    if (assistantId) {
      restoreEditingSnapshot();
      setIsPromptOpen(false);
      return;
    }

    resetPromptContentState();
    resetModelContentState();
  };

  const handleAdvanceToPrompt = async () => {
    setShowValidationErrors(true);

    if (!isFormValid) {
      return;
    }

    if (assistantId) {
      await handleConcludePrompt({ closeModelAfterSave: true });
      return;
    }

    setIsPopupOpen(false);
    setIsPromptOpen(true);
  };

  const handleBackToModel = () => {
    setIsPromptOpen(false);
    setIsPopupOpen(true);
  };

  const [assistantType, setAssistantType] = useState("");
  const [inputsSelected, setInputsSelected] = useState({
    pdf: false,
    word: false,
    excel: false,
  });
  const [outputsSelected, setOutputsSelected] = useState({
    excel: false,
    json: false,
    csv: false,
  });
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [isSavingClassifier, setIsSavingClassifier] = useState(false);

  useEffect(() => {
    const mapInputSelectionFromConfig = (inputFormat) => {
      const values = Array.isArray(inputFormat)
        ? inputFormat
        : inputFormat
          ? [inputFormat]
          : [];

      const normalized = values
        .map((value) => String(value).toLowerCase())
        .filter(Boolean);

      return {
        pdf: normalized.includes("pdf"),
        word:
          normalized.includes("docx"),
        excel:
          normalized.includes("excel") ||
          normalized.includes("xlsx") ||
          normalized.includes("csv"),
      };
    };

    const mapOutputSelectionFromConfig = (outputFormat) => {
      const value = String(outputFormat || "").toLowerCase();

      return {
        excel: value === "excel" || value === "xlsx",
        json: value === "json",
        csv: value === "csv",
      };
    };

    const normalizeDescription = (description) => {
      if (Array.isArray(description)) {
        if (description.length === 0) return "";
        if (typeof description[0] === "string") return description[0] || "";

        const first = description[0];
        if (first && typeof first === "object") {
          return (
            first.portuguese ||
            first.english ||
            first.french ||
            Object.values(first)[0] ||
            ""
          );
        }

        return "";
      }

      return typeof description === "string" ? description : "";
    };

    const resolveIconKey = (iconValue, iconMap) => {
      if (!iconValue) return null;

      const keys = Object.keys(iconMap);
      const matched = keys.find((key) => iconMap[key]() === iconValue);
      return matched || null;
    };

    const fetchClassifierConfig = async () => {
      if (!assistantId || !accessToken) {
        return;
      }

      try {
        const formData = new FormData();
        formData.append("assistant_id", assistantId);

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/post_get_config`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            body: formData,
          },
        );

        if (!response.ok) {
          throw new Error("Erro ao carregar configuração do classificador.");
        }

        const config = await response.json();

        const description = normalizeDescription(config?.description);
        const inputSelection = mapInputSelectionFromConfig(
          config?.input_format,
        );
        const outputSelection = mapOutputSelectionFromConfig(
          config?.output_format,
        );
        const mappedAssistantType = mapConfigTypeToUiType(config?.type);

        setAssistantData({
          assistant: {
            name: config?.name || "",
            description,
          },
        });

        setSelectedModel(config?.model || "gemini-3.5-flash-lite");
        setPromptText(config?.prompt_user || "");
        setAssistantType(mappedAssistantType);
        setInputsSelected(inputSelection);
        setOutputsSelected(outputSelection);

        const iconKey = resolveIconKey(config?.icon, icons);
        setActiveIcon(iconKey);

        if (inputSelection.excel) {
          setIsXlsxPanelOpen(false);

          const includedColumns = Array.isArray(config?.include)
            ? config.include
            : config?.include
              ? [config.include]
              : [];

          setSelectedXlsxColumns(includedColumns);

          setEditingSnapshot({
            name: config?.name || "",
            description,
            model: config?.model || "gemini-3.5-flash-lite",
            prompt_user: config?.prompt_user || "",
            assistantType: mappedAssistantType,
            inputsSelected: inputSelection,
            outputsSelected: outputSelection,
            activeIcon: iconKey,
            selectedXlsxColumns: includedColumns,
          });
        } else {
          resetXlsxFlow();

          setEditingSnapshot({
            name: config?.name || "",
            description,
            model: config?.model || "gemini-3.5-flash-lite",
            prompt_user: config?.prompt_user || "",
            assistantType: mappedAssistantType,
            inputsSelected: inputSelection,
            outputsSelected: outputSelection,
            activeIcon: iconKey,
            selectedXlsxColumns: [],
          });
        }
      } catch (error) {
        console.error("Erro ao buscar configuração do classificador:", error);
      }
    };

    fetchClassifierConfig();
  }, [assistantId, accessToken, icons]);

  const isInputSelectionValid = (inputs) => {
    const { pdf, word, excel } = inputs;

    if (excel) {
      return !pdf && !word;
    }

    return pdf || word;
  };

  const isOutputSelectionValid = (outputs) => {
    return Object.values(outputs).filter(Boolean).length === 1;
  };

  const validationErrors = {
    name: !(assistantData.assistant?.name || "").trim(),
    description: !(assistantData.assistant?.description || "").trim(),
    type: !assistantType,
    input: !isInputSelectionValid(inputsSelected),
    output: !isOutputSelectionValid(outputsSelected),
  };

  const isFormValid =
    (assistantData.assistant?.name || "").trim() &&
    (assistantData.assistant?.description || "").trim() &&
    assistantType &&
    isInputSelectionValid(inputsSelected) &&
    isOutputSelectionValid(outputsSelected);

  const resolveInputFormat = () => {
    const formats = [];

    if (inputsSelected.excel) formats.push("xlsx");
    if (inputsSelected.pdf) formats.push("pdf");
    if (inputsSelected.word) formats.push("docx");

    return formats.length > 0 ? formats : "";
  };

  const resolveOutputFormat = () => {
    if (outputsSelected.excel) return "xlsx";
    if (outputsSelected.json) return "json";
    if (outputsSelected.csv) return "csv";
    return "";
  };

  const resolveAssistantIcon = () => {
    if (!activeIcon || !icons[activeIcon]) {
      return null;
    }

    return icons[activeIcon]();
  };

  const mapUiTypeToConfigType = (uiType) => {
    if (uiType === "extractor") return "get_data";
    if (uiType === "classifier" && (inputsSelected.pdf || inputsSelected.word)) return "classify_doc";
    if (uiType === "classifier" && inputsSelected.excel) return "classify_row";
    return "";
  };

  const mapConfigTypeToUiType = (configType) => {
    const normalized = String(configType || "").toLowerCase();

    if (normalized === "get_data" || normalized === "extractor") {
      return "extractor";
    }

    if (
      normalized === "classify_row" ||
      normalized === "classify_doc" ||
      normalized === "classifier"
    ) {
      return "classifier";
    }

    return "";
  };
  const handleConcludePrompt = async ({ closeModelAfterSave = false } = {}) => {
    if (!isFormValid || isSavingClassifier) {
      return;
    }

    setIsSavingClassifier(true);

    try {
      const assistantName = (assistantData.assistant?.name || "").trim();
      const assistantDescription = (
        assistantData.assistant?.description || ""
      ).trim();

      const inputFormat = resolveInputFormat();
      const outputFormat = resolveOutputFormat();

      if (!assistantId) {
        const createPayload = {
          email,
          name: assistantName,
          description: assistantDescription,
          model: selectedModel,
          type_assistant: mapUiTypeToConfigType(assistantType),
          prompt_user: promptText,
          input_format: inputFormat,
          icon: resolveAssistantIcon(),
          include:
            inputsSelected.excel && selectedXlsxColumns.length > 0
              ? selectedXlsxColumns
              : null,
          exclude: null,
          output_format: outputFormat,
        };

        const createResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/post_create_classifier`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(createPayload),
          },
        );

        if (!createResponse.ok) {
          const errorText = await createResponse.text();
          throw new Error(errorText || "Erro ao criar classificador.");
        }

        const created = await createResponse.json();
        const createdAssistantId =
          created?.data?._id ?? created?.data?.id ?? created?._id;

        if (!createdAssistantId) {
          throw new Error("Classificador criado sem ID retornado.");
        }

        // Injeta otimisticamente no Hub para aparecer imediatamente ao navegar
        setAssistantList(prev => [
          ...prev,
          {
            id: createdAssistantId,
            type: "classifier",
            icon: resolveAssistantIcon(),
            nome: assistantName,
            description: [assistantDescription],
            adms: [{ email }],
            status: false,
          }
        ]);

        setAssistant(assistantName);
        setAssistantId(createdAssistantId);
      } else {
        const updateConfigPayload = {
          data: {
            _id: assistantId,
            "assistant.name": assistantName,
            "assistant.description": [assistantDescription],
            "assistant.icon": resolveAssistantIcon(),
            "info_model.model": selectedModel,
            updated_at: new Date().toISOString().split("T")[0],
            type: mapUiTypeToConfigType(assistantType),
            form: inputsSelected.excel ? "row" : "file",
            prompt_user: promptText,
            include:
              inputsSelected.excel && selectedXlsxColumns.length > 0
                ? selectedXlsxColumns
                : null,
            exclude: null,
            input_format: Array.isArray(inputFormat) ? inputFormat : (inputFormat ? [inputFormat] : []),
            output_format: outputFormat,
          },
        };

        const updateConfigResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/put_editing_config`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(updateConfigPayload),
          },
        );

        if (!updateConfigResponse.ok) {
          const errorText = await updateConfigResponse.text();
          throw new Error(
            errorText || "Erro ao editar configuração do classificador.",
          );
        }

        await updateConfigResponse.json();

        setEditingSnapshot({
          name: assistantName,
          description: assistantDescription,
          model: selectedModel,
          prompt_user: promptText,
          assistantType,
          inputsSelected: { ...inputsSelected },
          outputsSelected: { ...outputsSelected },
          activeIcon,
          selectedXlsxColumns:
            inputsSelected.excel && selectedXlsxColumns.length > 0
              ? [...selectedXlsxColumns]
              : [],
        });
      }

      if (closeModelAfterSave) {
        setIsPopupOpen(false);
      }

      setIsPromptOpen(false);
      setConfigUpdatedAt(Date.now());
    } catch (error) {
      console.error("Erro ao concluir configuração do classificador:", error);
      alert("Nao foi possivel salvar a configuracao do classificador.");
    } finally {
      setIsSavingClassifier(false);
    }
  };

  return (
    <div className="category-box" data-testid="category-box">
      <div className="category-margem">
        <div className="category-left-wrapper">
          <ConfigurationTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            tabs={[
              {
                id: "tab1",
                label: t("tab_simulation"),
                iconActive: "/imagens/simulation-blue.svg",
                iconInactive: "/imagens/simulation.svg",
                iconClassName: "icon-advanced-setting",
                disabled: false,
                iconAlt: t("tab_simulation"),
              },
              {
                id: "tab2",
                label: t("tab_users"),
                iconActive: "/imagens/three-users-active.svg",
                iconInactive: "/imagens/three-users.svg",
                iconClassName: "icon-three-users",
                disabled: false,
                iconAlt: t("tab_users"),
              },
            ]}
          >
            <div className="buttons-wrapper">
              <button className="btn-model" onClick={openPopup}>
                <img
                  src="/imagens/btn-model.svg"
                  alt={t("alt_model_icon")}
                  className="img-btn"
                />
                <p className="paragraph-model">{t("tab_model")}</p>
              </button>
              <button className="btn-prompt" onClick={openPrompt}>
                <img
                  src="/imagens/btn-prompt.svg"
                  alt={t("alt_prompt_icon")}
                  className="img-btn"
                />
                <p className="paragraph-prompt">{t("tab_prompt")}</p>
              </button>
            </div>
          </ConfigurationTabs>
          <CategoryDatabase
            classeCSS={`category-database ${activeTab === "tab1" ? "tab-1-active" : activeTab === "tab2" ? "tab-2-active" : ""}`}
            user={user}
            assistantId={assistantId}
            sessionId={sessionId}
            cost={totalCost}
            setCost={setTotalCost}
            activeTab={activeTab}
            configUpdatedAt={configUpdatedAt}
          />
        </div>

        <CategoryDefinition
          classeCSS={"category-definition"}
          assistantId={assistantId}
        />
        {isPopupOpen && (
          <div className="model-overlay">
            <div
              className={`model-content ${isSavingClassifier ? "saving" : ""}`}
              onClick={(e) => e.stopPropagation()}
            >
              <header className="model-header">
                <div className="model-wrapper">
                  <img
                    src="/imagens/btn-model-black.svg"
                    alt={t("alt_model_icon")}
                    className="icon-model"
                  />
                  <p className="title-model">{t("tab_model")}</p>
                </div>
                <img
                  src="/imagens/x-black.svg"
                  alt={t("alt_exit_icon")}
                  className="icon-exit"
                  onClick={() => {
                    if (!isSavingClassifier) {
                      closePopup();
                    }
                  }}
                />
              </header>
              <div className="articles-wrapper">
                <article className="first-article">
                  <div className="first-one">
                    <Modelos
                      defaultModel={selectedModel}
                      setModel={setSelectedModel}
                      width="100%"
                    />

                    <p className="first-one-subtitle">{t("profile_image")}</p>
                    <IconLibrary
                      icons={icons}
                      activeIcon={activeIcon}
                      setActiveIcon={setActiveIcon}
                      handleUpdate={handleIconUpdate}
                    />
                    <p
                      className={`new-subtitle required ${showValidationErrors && validationErrors.type ? "error" : ""}`}
                    >
                      {t("types_assistant")}
                    </p>
                    <div className="radio-wrapper">
                      <div className="itens-wrapper">
                        <input
                          type="radio"
                          name="type-assistant"
                          id="classifier"
                          value="classifier"
                          checked={assistantType === "classifier"}
                          onClick={() =>
                            setAssistantType((prev) =>
                              prev === "classifier" ? "" : "classifier",
                            )
                          }
                          onChange={() => setAssistantType("classifier")}
                        />
                        <label htmlFor="classifier">{t("classifier")}</label>
                      </div>
                      <div className="itens-wrapper">
                        <input
                          type="radio"
                          name="type-assistant"
                          id="extractor"
                          value="extractor"
                          checked={assistantType === "extractor"}
                          onClick={() =>
                            setAssistantType((prev) =>
                              prev === "extractor" ? "" : "extractor",
                            )
                          }
                          onChange={() => setAssistantType("extractor")}
                        />
                        <label htmlFor="extractor">{t("data_extractor")}</label>
                      </div>
                    </div>
                  </div>
                  <div className="line"></div>
                  <SecondBox
                    tSelfAssist={tSelfAssist}
                    showValidationErrors={showValidationErrors}
                    validationErrors={validationErrors}
                    assistant={assistantData}
                    handleUpdate={handleUpdateAssistant}
                    normalizedAssistantDescription={assistantData.assistant.description || ""}
                  />
                </article>
                {inputsSelected.excel && isXlsxPanelOpen ? (
                  <div id="excel-panel" className="second-article">
                    <div className="excel-wrapper-title">
                      <p className="subtitle-excel">
                        {t("reference_spreadsheet")}
                      </p>
                      <img
                        src="/imagens/info.svg"
                        className="info-icon"
                        title={t("reference_spreadsheet_info")}
                        alt="Info"
                      />
                    </div>
                    {!isXlsxProcessing && xlsxColumns.length === 0 && (
                      <FileUpload
                        showCloseIcon={true}
                        onClose={closeUploadPanel}
                        acceptedTypes={[".xlsx"]}
                        onFilesSelected={handleXlsxUpload}
                        multiple={false}
                      />
                    )}

                    {isXlsxProcessing && (
                      <div className="xlsx-columns-panel">
                        <img
                          src="/imagens/x-black.svg"
                          alt={t("alt_exit_icon")}
                          className="xlsx-panel-close-icon"
                          onClick={closeUploadPanel}
                        />
                        <div className="xlsx-columns-loading">
                          <div className="loading-spinner category-loading-spinner"></div>
                          <p className="xlsx-columns-loading-text">
                            Processando arquivo XLSX. Aguarde...
                          </p>
                        </div>
                      </div>
                    )}

                    {!isXlsxProcessing && xlsxColumns.length > 0 && (
                      <div className="xlsx-columns-panel">
                        <img
                          src="/imagens/x-black.svg"
                          alt={t("alt_exit_icon")}
                          className="xlsx-panel-close-icon"
                          onClick={closeUploadPanel}
                        />
                        <p className="xlsx-columns-title">
                          Selecione as colunas
                        </p>
                        <div className="xlsx-columns-list-wrapper">
                          <label className="xlsx-columns-item select-all">
                            <input
                              type="checkbox"
                              className="xlsx-columns-checkbox-input"
                              checked={
                                xlsxColumns.length > 0 &&
                                selectedXlsxColumns.length ===
                                  xlsxColumns.length
                              }
                              onChange={handleToggleAllXlsxColumns}
                            />
                            <img
                              src={
                                xlsxColumns.length > 0 &&
                                selectedXlsxColumns.length ===
                                  xlsxColumns.length
                                  ? "/imagens/checkbox-checked.svg"
                                  : "/imagens/checkbox-unchecked.svg"
                              }
                              alt=""
                              aria-hidden="true"
                              className="xlsx-columns-checkbox-icon"
                            />
                            <span>Selecionar todas</span>
                          </label>

                          {xlsxColumns.map((columnName, index) => {
                            const itemKey = `${columnName}-${index}`;
                            const isChecked =
                              selectedXlsxColumns.includes(columnName);

                            return (
                              <label
                                className="xlsx-columns-item"
                                key={itemKey}
                              >
                                <input
                                  type="checkbox"
                                  className="xlsx-columns-checkbox-input"
                                  checked={isChecked}
                                  onChange={() =>
                                    handleToggleXlsxColumn(columnName)
                                  }
                                />
                                <img
                                  src={
                                    isChecked
                                      ? "/imagens/checkbox-checked.svg"
                                      : "/imagens/checkbox-unchecked.svg"
                                  }
                                  alt=""
                                  aria-hidden="true"
                                  className="xlsx-columns-checkbox-icon"
                                />
                                <span>{columnName}</span>
                              </label>
                            );
                          })}
                        </div>
                        <button
                          className="xlsx-columns-save-btn"
                          onClick={handleSaveXlsxColumns}
                          disabled={selectedXlsxColumns.length === 0}
                        >
                          Salvar
                        </button>
                      </div>
                    )}

                    {!isXlsxProcessing && xlsxUploadError && (
                      <p className="xlsx-columns-error">{xlsxUploadError}</p>
                    )}
                  </div>
                ) : (
                  <article className="second-article">
                    <p
                      className={`new-subtitle required ${showValidationErrors && validationErrors.input ? "error" : ""}`}
                    >
                      Input
                    </p>
                    <div className="group-item">
                      <div className="itens-wrapper">
                        <input
                          type="checkbox"
                          name="pdf"
                          id="input-pdf"
                          className="checkbox-input"
                          checked={inputsSelected.pdf}
                          onChange={() =>
                            setInputsSelected((prev) => {
                              const nextPdf = !prev.pdf;

                              if (nextPdf && prev.excel) {
                                resetXlsxFlow();
                              }

                              return {
                                ...prev,
                                pdf: nextPdf,
                                excel: nextPdf ? false : prev.excel,
                              };
                            })
                          }
                        />
                        <label htmlFor="input-pdf">PDF</label>
                      </div>
                      <div className="itens-wrapper">
                        <input
                          type="checkbox"
                          name="word"
                          id="input-word"
                          className="checkbox-input"
                          checked={inputsSelected.word}
                          onChange={() =>
                            setInputsSelected((prev) => {
                              const nextWord = !prev.word;

                              if (nextWord && prev.excel) {
                                resetXlsxFlow();
                              }

                              return {
                                ...prev,
                                word: nextWord,
                                excel: nextWord ? false : prev.excel,
                              };
                            })
                          }
                        />
                        <label htmlFor="input-word">Word</label>
                      </div>
                      <div className={`itens-wrapper ${inputsSelected.pdf || inputsSelected.word ? "disabled" : ""}`}>
                        <input
                          type="checkbox"
                          name="excel"
                          id="input-excel"
                          className="checkbox-input"
                          checked={inputsSelected.excel}
                          disabled={inputsSelected.pdf || inputsSelected.word}
                          onChange={() =>
                            setInputsSelected((prev) => {
                              const nextExcel = !prev.excel;

                              if (!nextExcel) {
                                resetXlsxFlow();
                              } else {
                                setIsXlsxPanelOpen(true);
                              }

                              return {
                                ...prev,
                                excel: nextExcel,
                                pdf: nextExcel ? false : prev.pdf,
                                word: nextExcel ? false : prev.word,
                              };
                            })
                          }
                        />
                        <label htmlFor="input-excel">Excel</label>
                      </div>
                    </div>
                    <p className="selection-rule-text">
                      {t("input_selection_rule")}
                    </p>
                    <p
                      className={`new-subtitle required ${showValidationErrors && validationErrors.output ? "error" : ""}`}
                    >
                      Output
                    </p>
                    <div className="group-item">
                      <div className="itens-wrapper">
                        <input
                          type="checkbox"
                          name="excel"
                          id="output-excel"
                          className="checkbox-input"
                          checked={outputsSelected.excel}
                          onChange={() =>
                            setOutputsSelected((prev) => {
                              if (prev.excel) {
                                return {
                                  excel: false,
                                  json: false,
                                  csv: false,
                                };
                              }

                              return { excel: true, json: false, csv: false };
                            })
                          }
                        />
                        <label htmlFor="output-excel">Excel</label>
                      </div>
                      <div className="itens-wrapper">
                        <input
                          type="checkbox"
                          name="json"
                          id="output-json"
                          className="checkbox-input"
                          checked={outputsSelected.json}
                          onChange={() =>
                            setOutputsSelected((prev) => {
                              if (prev.json) {
                                return {
                                  excel: false,
                                  json: false,
                                  csv: false,
                                };
                              }

                              return { excel: false, json: true, csv: false };
                            })
                          }
                        />
                        <label htmlFor="output-json">JSON</label>
                      </div>
                      <div className="itens-wrapper">
                        <input
                          type="checkbox"
                          name="csv"
                          id="output-csv"
                          className="checkbox-input"
                          checked={outputsSelected.csv}
                          onChange={() =>
                            setOutputsSelected((prev) => {
                              if (prev.csv) {
                                return {
                                  excel: false,
                                  json: false,
                                  csv: false,
                                };
                              }

                              return { excel: false, json: false, csv: true };
                            })
                          }
                        />
                        <label htmlFor="output-csv">CSV</label>
                      </div>
                    </div>
                    <p className="selection-rule-text">
                      {t("output_selection_rule")}
                    </p>
                    {/* <p className="new-subtitle">{t("analysis_folder")}</p>
                    <div className="btns-wrapper">
                      <button className="btn-onedrive">
                        <img
                          src="/imagens/onedrive.svg"
                          alt="onedrive-icon"
                          className="img-onedrive"
                        />
                        Onedrive
                      </button>
                      <button className="btn-sharepoint">
                        <img
                          src="/imagens/sharepoint.svg"
                          alt="sharepoint-icon"
                          className="img-sharepoint"
                        />
                        Sharepoint
                      </button>
                    </div> */}
                  </article>
                )}
              </div>
              {/** Botão visualmente liberado apenas quando o formulário estiver válido */}
              <button
                className={`button-save-model ${isFormValid ? "enabled" : ""}`}
                onClick={handleAdvanceToPrompt}
                disabled={isSavingClassifier}
              >
                {isSavingClassifier
                  ? t("saving")
                  : isEditingClassifier
                    ? t("save")
                    : t("advance")}
              </button>
            </div>
          </div>
        )}
        {isPromptOpen && (
          <div className="prompt-overlay">
            <div
              className={`prompt-content ${isSavingClassifier ? "saving" : ""}`}
              onClick={(e) => e.stopPropagation()}
            >
              <header className="prompt-wrapper">
                <div className="prompt-header">
                  <img
                    src="/imagens/icon-prompt.svg"
                    alt="Promp Icon"
                    className="header-icon-prompt"
                  />
                  <p className="title-prompt">{t("general_prompt")}</p>
                  <img
                    src="/imagens/info.svg"
                    alt="Info Icon"
                    className="header-icon-info"
                    title={t("brief_prompt_info")}
                  />
                </div>
                <img
                  src="/imagens/x-black.svg"
                  alt={t("alt_exit_icon")}
                  className="icon-exit"
                  onClick={() => {
                    if (!isSavingClassifier) {
                      closePrompt();
                    }
                  }}
                />
              </header>
              <textarea
                name=""
                id=""
                className={`textarea-prompt ${promptText.trim() ? "filled" : ""}`}
                placeholder={t("prompt_general")}
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                disabled={isSavingClassifier}
              />

              <footer className="prompt-btn-wrapper">
                {!isEditingClassifier && (
                  <button
                    className="btn-back-prompt"
                    onClick={handleBackToModel}
                    disabled={isSavingClassifier}
                  >
                    <img src="/imagens/icon-back.svg" alt="" />
                  </button>
                )}
                <button
                  className="btn-conclude-prompt"
                  onClick={handleConcludePrompt}
                  disabled={isSavingClassifier}
                >
                  {isSavingClassifier
                    ? t("saving")
                    : isEditingClassifier
                      ? t("save")
                      : t("conclude")}
                </button>
              </footer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Category;
